<script>
    import { getContext, onDestroy, onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import { api_info, fetchWithAuthentication } from "../api/client.js";
    import EditDetailModal from "./EditDetailModal.svelte";

    const context = getContext(api_info);

    let collection_name;
    let new_collection_name;
    let db_name;
    let loading = true;
    let page_no = 1;
    let nb_of_pages = 1;
    let nb_of_records = 0;
    let page_size = 1000;
    let items = [];
    let fields = ["subject", "detail", "type_name"];
    let oldData = {};
    let newData = {};
    let showEditDialog = false;
    let renamingCollection = false;
    $: first_item_no = (page_no - 1) * page_size + 1;
    $: last_item_no = Math.min(page_no * page_size, nb_of_records);

    async function getData() {
        loading = true;
        const response = await fetchWithAuthentication(context, "/get_data/", {
            collection_name,
            db_name,
            page_no,
        });
        nb_of_pages = response.nb_of_pages;
        items = response.data;
        nb_of_records = response.nb_of_records;
        loading = false;
    }
    function previousPage() {
        if (page_no > 1) {
            page_no -= 1;
            getData();
        }
    }
    function nextPage() {
        if (page_no < nb_of_pages) {
            page_no += 1;
            getData();
        }
    }
    function changePage(event) {
        if (event.keyCode === 13) {
            const page = Number(event.target.value);
            if (page > 0 && page <= nb_of_pages) {
                page_no = page;
                getData();
            } else {
                event.target.value = page_no;
            }
        }
    }
    function addData() {
        oldData = {};
        newData = {};
        for (const field of fields) {
            newData[field] = "";
        }
        showEditDialog = true;
    }
    function editData(item) {
        oldData = { ...item };
        newData = { ...item };
        delete newData["_id"];
        showEditDialog = true;
    }
    function onAddSuccess() {
        getData();
    }
    function onEditSuccess(event) {
        const { data } = event.detail;
        const index = items.findIndex((x) => x._id === data._id);
        if (index > -1) {
            items[index] = data;
        }
    }
    async function deleteData(item) {
        if (confirm("คุณต้องการลบข้อมูลหรือไม่?")) {
            try {
                const response = await fetchWithAuthentication(
                    context,
                    "/delete_record/",
                    {
                        db_name,
                        collection_name,
                        data: item,
                    }
                );
                if (response.status) {
                    items = items.filter((x) => x !== item);
                }
            } catch (e) {
                alert("ไม่สามารถลบข้อมูลได้");
            }
        }
    }
    async function renameCollection() {
        new_collection_name = new_collection_name.trim();
        if (new_collection_name === collection_name) {
            // do nothing
        } else {
            if (confirm("ยืนยันเปลี่ยนชื่อไฟล์?")) {
                try {
                    renamingCollection = true;
                    const response = await fetchWithAuthentication(
                        context,
                        "/rename_collection/",
                        {
                            db_name,
                            old_collection_name: collection_name,
                            new_collection_name,
                        }
                    );
                    if (response.status) {
                        alert("เปลี่ยนชื่อไฟล์เรียบร้อยแล้ว");
                    }
                    navigate(
                        `/edit_data?db_name=${db_name}&filename=${encodeURIComponent(
                            new_collection_name
                        )}`,
                        { replace: true }
                    );
                } catch {
                    alert("ไม่สามารถเปลี่ยนชื่อไฟล์ได้");
                } finally {
                    renamingCollection = false;
                }
            }
        }
    }

    onMount(() => {
        document.documentElement.classList.add("full");
        document.body.classList.add("full");
        document.getElementById("main-container").classList.add("full");
        document.getElementById("main-div").classList.add("full");
        const params = new URLSearchParams(window.location.search);
        collection_name = params.get("filename");
        new_collection_name = collection_name;
        db_name = params.get("db_name");
        if (db_name === "raw_data") {
            fields = ["subject", "detail", "type_name"];
        } else if (db_name === "data_with_tags") {
            fields = ["subject", "detail", "type_name", "tags"];
            //TODO: should we show date, department, type, year
        }
        getData();
    });
    onDestroy(() => {
        document.documentElement.classList.remove("full");
        document.body.classList.remove("full");
        document.getElementById("main-container").classList.remove("full");
        document.getElementById("main-div").classList.remove("full");
    });
</script>

<form class="form-horizontal">
    <div class="form-group">
        <label for="version" class="col-sm-2 control-label">ชื่อไฟล์</label>
        <div class="col-sm-5">
            <input
                type="text"
                class="form-control"
                id="version"
                bind:value={new_collection_name}
            />
        </div>
        <div class="col-sm-5">
            <button
                type="button"
                class="btn btn-default"
                on:click={renameCollection}
                disabled={renamingCollection}
            >
                {#if renamingCollection}
                    <span class="glyphicon glyphicon-refresh spinning" />
                {/if}
                <span class="glyphicon glyphicon-pencil" /> เปลี่ยนชื่อไฟล์
            </button>
        </div>
    </div>
    <div class="form-group">
        <label class="col-sm-2 control-label">ข้อมูล</label>
        <div class="col-sm-6">
            <button type="button" class="btn btn-primary" on:click={addData}>
                <span class="glyphicon glyphicon-plus" /> เพิ่มข้อมูล
            </button>
        </div>
        <div class="col-sm-4 number-of-items">
            {#if nb_of_records > 0}
                {first_item_no}-{last_item_no}/{nb_of_records} รายการ
            {/if}
        </div>
    </div>
    <div class="data-list">
        <div class="col-sm-offset-2 col-sm-10">
            {#if loading}
                <span class="glyphicon glyphicon-refresh spinning" /> กำลังโหลดข้อมูล
            {:else}
                {#each items as item (item._id)}
                    <div class="data-item">
                        <div class="data-content">
                            {#each fields as field}
                                {field}: {item[field]}<br />
                            {/each}
                        </div>
                        <div>
                            <button
                                class="btn"
                                type="button"
                                on:click={() => editData(item)}
                            >
                                <span
                                    class="glyphicon glyphicon-pencil"
                                /></button
                            >
                            <button
                                class="btn"
                                type="button"
                                on:click={() => deleteData(item)}
                            >
                                <span
                                    class="glyphicon glyphicon-remove"
                                /></button
                            >
                        </div>
                    </div>
                {/each}
            {/if}
        </div>
    </div>
    <div class="form-group">
        <div class="col-sm-offset-2 col-sm-10 paginator">
            <button
                class="btn"
                type="button"
                on:click={previousPage}
                disabled={page_no === 1}
                ><span class="glyphicon glyphicon-triangle-left" /></button
            >

            หน้า

            <input type="text" value={page_no} size={3} on:keyup={changePage} />
            /{nb_of_pages}
            <button
                class="btn"
                type="button"
                on:click={nextPage}
                disabled={page_no === nb_of_pages}
            >
                <span class="glyphicon glyphicon-triangle-right" /></button
            >
        </div>
    </div>
</form>

<EditDetailModal
    {fields}
    old_data={oldData}
    data={newData}
    bind:show={showEditDialog}
    on:add_success={onAddSuccess}
    on:edit_success={onEditSuccess}
    {db_name}
    {collection_name}
/>

<style>
    .data-list {
        margin-top: 10px;
        overflow-y: scroll;
        flex: 1;
    }
    .data-item {
        border-radius: 5px;
        padding: 10px;
        background-color: #d1e4fc;
        margin-bottom: 5px;
        display: flex;
    }
    .data-content {
        flex: 1;
    }

    :global(html.full),
    :global(body.full),
    :global(.container.full) {
        height: 100%;
    }
    :global(.container.full) {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    :global(#main-div.full) {
        flex: 1;
        overflow: hidden;
    }
    .form-horizontal {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    .paginator {
        margin-top: 10px;
        text-align: center;
    }
    .number-of-items {
        padding-top: 6px;
        text-align: right;
    }
</style>
